import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Plus, Search, X } from "lucide-react";
import { useForm, FormProvider } from "react-hook-form";
import toast from "react-hot-toast";
import MasterModal from "./MasterModal";
import MasterTable from "./MasterTable";
import { getStates, createState, updateState, toggleStateActive } from "@/services/masters";
import AddStateForm, { StateFormData } from "./states/AddState";
import { useScreenPermission } from "@/hooks/useScreenPermission";
import MasterTableHeader from "./generics/MasterTableHeader";

interface State {
  id?: string | number;
  name: string;
  code: string;
  isActive: boolean;
}

export default function States() {
  const { canCreate, canUpdate } = useScreenPermission("states");
  const [states, setStates] = useState<State[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingState, setEditingState] = useState<State | null>(null);
  const [togglingId, setTogglingId] = useState<string | number | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const isInitialMount = useRef(true);
  const [totalCount,setTotalCount]=useState(0);
  const [statusFilter, setStatusFilter]=useState<string>("");

  // Form instance
  const methods = useForm<StateFormData>({
    defaultValues: {
      Name: "",
      State_Code: "",
    }
  });

    const loadStates = useCallback(async (p: number = 1, search: string = "",statusFilter?: string) => {
    setIsLoading(true);
    try {
      const { data, status } = await getStates(p, search || undefined,statusFilter);
      if (status === 200 && data) {
        const d = data as any;
        const items: any[] = d.results ?? (Array.isArray(d) ? d : []);
        setStates(items.map((item: any) => ({
          id: item.id,
          name: item.Name,
          code: item.State_Code,
          isActive: item.Is_Active ?? true,
        })));
        setTotalPages(d.total_pages ?? 1);
        setTotalCount(d.count ?? 0);
      } else {
        // toast.error("Failed to load states");
      }
    } catch (error) {
      toast.error("Error loading states");
    } finally {
      setIsLoading(false);
    }
  }, []);


  useEffect(() => {
  if (isInitialMount.current) {
    isInitialMount.current = false;
    loadStates(1, "", statusFilter);
    return;
  }

  const id = setTimeout(() => {
    setPage(1);
    loadStates(1, searchQuery, statusFilter);
  }, 500);

  return () => clearTimeout(id);
}, [searchQuery, statusFilter, loadStates]);

  const handleOpenModal = useCallback((state?: State) => {
    if (state) {
      const foundState = states.find(s => s.id === state.id);
      if (foundState) {
        const resetValues = {
          Name: foundState.name,
          State_Code: foundState.code,
        };
        methods.reset(resetValues);
        setEditingState(foundState);
      }
    } else {
      methods.reset({
        Name: "",
        State_Code: "",
      });
      setEditingState(null);
    }
    setIsModalOpen(true);
  }, [states, methods]);

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setEditingState(null);
    methods.reset();
  }, [methods]);

  const handleSubmit = useCallback(async (data: StateFormData) => {
    methods.clearErrors();
    setIsLoading(true);
    try {
      const response = editingState?.id
        ? await updateState(Number(editingState.id), data)
        : await createState(data);

      if (response.status === 201 || response.status === 200) {
        toast.success(editingState ? "State updated successfully" : "State created successfully");
        handleCloseModal();
        await loadStates(page, searchQuery, statusFilter);
      } else if (response.errors && typeof response.errors === "object") {
        let hasFieldErrors = false;
        Object.entries(response.errors).forEach(([key, value]) => {
          const errorText = Array.isArray(value) ? value[0] : String(value);
          if (key === "error" || key === "non_field_errors" || key === "detail") {
            toast.error(errorText);
          } else {
            methods.setError(key as keyof StateFormData, { type: "server", message: errorText });
            hasFieldErrors = true;
          }
        });
        if (!hasFieldErrors) {
          toast.error("Failed to save state");
        }
      } else {
        toast.error("Failed to save state");
      }
    } catch (error) {
      toast.error("Error saving state");
    } finally {
      setIsLoading(false);
    }
  }, [editingState, handleCloseModal, loadStates, methods]);

 const handleToggleActive = useCallback(async (state: State) => {
  if (!state.id) return;

  setTogglingId(state.id);

  try {
    const response = await toggleStateActive(Number(state.id), {
      Is_Active: !state.isActive,
    });

    if (response.status === 200) {
      toast.success(
        `State ${!state.isActive ? "activated" : "deactivated"} successfully`
      );

      await loadStates(page, searchQuery, statusFilter);
    } else {
      toast.error("Failed to update state status");
    }
  } catch {
    toast.error("Error updating state status");
  } finally {
    setTogglingId(null);
  }
}, [loadStates, page, searchQuery, statusFilter]);

  const columns = useMemo(() => [
    { key: "name" as const, label: "Name" },
    { key: "code" as const, label: "Code" },
  ], []);

  return (
    <div className="space-y-6">
       <MasterTableHeader
        title="States Management"
        description="Create, edit, and manage states"
        canCreate={canCreate}
        addButtonText="Add New State"
        onAdd={() => handleOpenModal()}
        searchPlaceholder="Search by name or code..."
        searchQuery={searchQuery}
        setSearchQuery={setSearchQuery}
        showDropdownFilter
        dropdownValue={statusFilter}
        onDropdownChange={(value) => {
          setStatusFilter(value);
          loadStates(1, searchQuery, value);
        }}
    showClearFilters
  onClearFilters={() => {
    setSearchQuery("");
    setStatusFilter("");
    setPage(1);

    loadStates(1, "", "");
  }}
      />

      <MasterTable
        columns={columns}
        data={states}
        onEdit={handleOpenModal}
        onToggleActive={handleToggleActive}
        togglingId={togglingId}
        isLoading={isLoading && states.length === 0}
        currentPage={page}
        totalPages={totalPages}
        onPageChange={(p) => { setPage(p); loadStates(p, searchQuery, statusFilter); }}
        canEdit={canUpdate}
        canToggle={canUpdate}
        totalCount={totalCount}
      />

      <MasterModal
        isOpen={isModalOpen}
        title={editingState ? "Edit State" : "Add New State"}
        onClose={handleCloseModal}
        onSubmit={methods.handleSubmit(handleSubmit)}
        isLoading={isLoading}
      >
        <FormProvider {...methods}>
          <AddStateForm />
        </FormProvider>
      </MasterModal>
    </div>
  );
}
